"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateAverageRating = exports.formatAgeRating = exports.formatMovieYear = exports.formatRuntime = exports.analyzeMovieListForDuplicates = exports.cleanupWatchlistDuplicates = exports.findDuplicateMovies = exports.migrateMovieId = exports.normalizeMovieObject = exports.extractCoreMovieId = exports.areMovieIdsEquivalent = exports.getStandardizedMovieId = exports.resolveTmdbQueueMovieId = exports.getTmdbNumericIdFromMovieRecord = exports.formatMovieDisplayTitleFromId = exports.getTmdbNumericIdForCache = void 0;
const dateUtils_1 = require("./dateUtils");
/** Legacy slug ids stored as `movie-12345` (e.g. bad imports) → TMDB numeric `12345`. */
function parseMovieDashNumericId(raw) {
    const m = /^movie-(\d+)$/i.exec(String(raw).trim());
    return m ? m[1] : null;
}
/** Strip repeated `tmdb_` / `tmdb` prefixes (e.g. `tmdb_tmdb_123` → `123`). */
function stripTmdbPrefixes(raw) {
    let s = raw.trim();
    while (s.startsWith('tmdb_') || s.startsWith('tmdb')) {
        if (s.startsWith('tmdb_')) {
            s = s.substring(5).trim();
        }
        else {
            s = s.substring(4).trim();
        }
    }
    return s;
}
/**
 * Numeric TMDB id for Firestore `movieCache` doc IDs and TMDB API paths.
 * Returns null for IMDb-only (`tt*`) or unrecognized strings.
 */
function getTmdbNumericIdForCache(movieId) {
    if (movieId == null)
        return null;
    const s = String(movieId).trim();
    if (!s || s === 'N/A')
        return null;
    const fromMovieDash = parseMovieDashNumericId(s);
    if (fromMovieDash)
        return fromMovieDash;
    if (/^\d+$/.test(s))
        return s;
    if (s.startsWith('tmdb')) {
        const stripped = stripTmdbPrefixes(s);
        if (/^\d+$/.test(stripped))
            return stripped;
    }
    return null;
}
exports.getTmdbNumericIdForCache = getTmdbNumericIdForCache;
/**
 * Short UI title when real TMDb metadata is missing (fallback rows, queue errors).
 * Avoids showing internal ids like `Movie tmdb_411054` in cards and lists.
 */
function formatMovieDisplayTitleFromId(movieId) {
    if (movieId == null)
        return 'Unknown title';
    const s = String(movieId).trim();
    if (!s || s === 'N/A')
        return 'Unknown title';
    const n = getTmdbNumericIdForCache(s);
    if (n)
        return `Movie #${n}`;
    if (/^tt\d+$/i.test(s))
        return `Movie (${s})`;
    return `Movie (${s})`;
}
exports.formatMovieDisplayTitleFromId = formatMovieDisplayTitleFromId;
function tryParseTmdbNumericCandidate(val) {
    if (val == null)
        return null;
    const s = String(val).trim();
    if (!s || s === 'N/A' || s === 'undefined')
        return null;
    const fromMovieDash = parseMovieDashNumericId(s);
    if (fromMovieDash)
        return fromMovieDash;
    if (/^\d+$/.test(s))
        return s;
    if (s.startsWith('tmdb')) {
        const stripped = stripTmdbPrefixes(s);
        if (/^\d+$/.test(stripped))
            return stripped;
    }
    return null;
}
/**
 * Reads a TMDB numeric id from a movie-like object (`id`, `imdbID` carrying `tmdb_*`, etc.).
 */
function getTmdbNumericIdFromMovieRecord(movie) {
    const idKeys = [
        movie.id,
        movie.movie_id,
        movie.tmdbId,
        movie.tmdb_id,
    ];
    for (const k of idKeys) {
        const n = tryParseTmdbNumericCandidate(k);
        if (n)
            return n;
    }
    const imdbish = [movie.imdbID, movie.imdb_id];
    for (const k of imdbish) {
        const n = tryParseTmdbNumericCandidate(k);
        if (n)
            return n;
    }
    return null;
}
exports.getTmdbNumericIdFromMovieRecord = getTmdbNumericIdFromMovieRecord;
function pickImdbTT(movie) {
    const candidates = [movie.id, movie.imdbID, movie.imdb_id];
    for (const c of candidates) {
        if (c == null)
            continue;
        const s = String(c).trim();
        if (s.startsWith('tt') && /^tt\d+$/.test(s))
            return s;
    }
    return null;
}
/**
 * Best id string to pass into TMDB queue / movie cache when hydrating from a watchlist row.
 * Prefers numeric TMDB; falls back to raw `id` / `imdbID` (e.g. `tt*`) for legacy rows.
 */
function resolveTmdbQueueMovieId(movie) {
    var _a, _b, _c, _d, _e;
    if (!movie)
        return '';
    const n = getTmdbNumericIdFromMovieRecord(movie);
    if (n)
        return n;
    const fromString = getTmdbNumericIdForCache(String((_c = (_b = (_a = movie.id) !== null && _a !== void 0 ? _a : movie.imdbID) !== null && _b !== void 0 ? _b : movie.imdb_id) !== null && _c !== void 0 ? _c : ''));
    if (fromString)
        return fromString;
    const raw = (_e = (_d = movie.id) !== null && _d !== void 0 ? _d : movie.imdbID) !== null && _e !== void 0 ? _e : movie.imdb_id;
    return raw != null ? String(raw).trim() : '';
}
exports.resolveTmdbQueueMovieId = resolveTmdbQueueMovieId;
/**
 * Canonical id for app storage: always `tmdb_<n>` when any TMDB numeric id is present on the record.
 * Falls back to `tt*` or `title_*` only when TMDB is unavailable (legacy OMDb-only rows).
 */
const getStandardizedMovieId = (movie) => {
    if (movie == null || movie === '')
        return 'unknown';
    if (typeof movie === 'string' || typeof movie === 'number') {
        const movieId = String(movie).trim();
        if (!movieId)
            return 'unknown';
        const movieDashNum = parseMovieDashNumericId(movieId);
        if (movieDashNum)
            return `tmdb_${movieDashNum}`;
        if (/^\d+$/.test(movieId))
            return `tmdb_${movieId}`;
        if (movieId.startsWith('tmdb')) {
            const stripped = stripTmdbPrefixes(movieId);
            if (/^\d+$/.test(stripped))
                return `tmdb_${stripped}`;
            return `tmdb_${stripped}`;
        }
        if (movieId.startsWith('tt') && /^tt\d+$/.test(movieId))
            return movieId;
        if (movieId.startsWith('title_'))
            return movieId;
        return movieId;
    }
    const tmdbNum = getTmdbNumericIdFromMovieRecord(movie);
    if (tmdbNum)
        return `tmdb_${tmdbNum}`;
    const imdbTT = pickImdbTT(movie);
    if (imdbTT)
        return imdbTT;
    if (movie.Title || movie.title) {
        const title = (movie.Title || movie.title)
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]/g, '')
            .substring(0, 50);
        const year = (movie.Year || movie.year || 'unknown').toString().trim();
        return `title_${title}_${year}`;
    }
    console.warn('getStandardizedMovieId: Unable to generate proper ID for movie:', movie);
    return 'unknown';
};
exports.getStandardizedMovieId = getStandardizedMovieId;
/**
 * Check if two movie IDs refer to the same movie
 */
const areMovieIdsEquivalent = (id1, id2) => {
    if (id1 === id2)
        return true;
    // Normalize both IDs
    const normalizedId1 = (0, exports.getStandardizedMovieId)({ imdbID: id1, id: id1 });
    const normalizedId2 = (0, exports.getStandardizedMovieId)({ imdbID: id2, id: id2 });
    return normalizedId1 === normalizedId2;
};
exports.areMovieIdsEquivalent = areMovieIdsEquivalent;
/**
 * Extract the core identifier from a movie ID for duplicate detection
 */
const extractCoreMovieId = (movieId) => {
    if (movieId.startsWith('tt')) {
        return movieId; // IMDb IDs are already canonical
    }
    if (movieId.startsWith('tmdb_')) {
        return movieId.substring(5); // Extract the numeric part
    }
    if (movieId.startsWith('tmdb')) {
        return movieId.substring(4); // Extract from old format
    }
    if (/^\d+$/.test(movieId)) {
        return movieId; // Already a numeric ID
    }
    return movieId; // Return as-is for other formats
};
exports.extractCoreMovieId = extractCoreMovieId;
/**
 * Normalizes a movie object to ensure consistent property names
 */
const normalizeMovieObject = (movie) => {
    return Object.assign(Object.assign({}, movie), { Title: movie.Title || movie.title, Year: movie.Year || movie.year, Poster: movie.Poster || movie.poster, imdbID: movie.imdbID || movie.imdb_id, id: movie.id });
};
exports.normalizeMovieObject = normalizeMovieObject;
/**
 * Migrate legacy movie id strings toward canonical `tmdb_<n>` (handles double-prefixed ids).
 */
const migrateMovieId = (oldId) => {
    if (!oldId)
        return oldId;
    const trimmedId = oldId.trim();
    if (trimmedId.startsWith('tt') && /^tt\d+$/.test(trimmedId)) {
        return trimmedId;
    }
    if (trimmedId.startsWith('title_')) {
        return trimmedId;
    }
    if (trimmedId.startsWith('tmdb')) {
        let normalized = trimmedId;
        while (normalized.startsWith('tmdb_') || normalized.startsWith('tmdb')) {
            if (normalized.startsWith('tmdb_')) {
                normalized = normalized.substring(5);
            }
            else if (normalized.startsWith('tmdb')) {
                normalized = normalized.substring(4);
            }
        }
        if (/^\d+$/.test(normalized)) {
            return `tmdb_${normalized}`;
        }
        return `tmdb_${normalized}`;
    }
    if (/^\d+$/.test(trimmedId)) {
        return `tmdb_${trimmedId}`;
    }
    return trimmedId;
};
exports.migrateMovieId = migrateMovieId;
/**
 * Find duplicate movies in a list based on standardized IDs
 */
const findDuplicateMovies = (movieDataCache) => {
    const groups = new Map();
    // Group movies by their core TMDb ID
    for (const [movieId, movieData] of movieDataCache.entries()) {
        const coreId = (0, exports.extractCoreMovieId)(movieId);
        if (!groups.has(coreId)) {
            groups.set(coreId, []);
        }
        groups.get(coreId).push(Object.assign({ movieId }, movieData));
    }
    // Return only groups with duplicates
    return Array.from(groups.entries())
        .filter(([_, movies]) => movies.length > 1)
        .map(([normalizedKey, duplicates]) => ({
        normalizedKey,
        duplicates
    }));
};
exports.findDuplicateMovies = findDuplicateMovies;
/**
 * Clean up duplicate movies from a watchlist
 */
const cleanupWatchlistDuplicates = (movieDetails) => {
    const seen = new Set();
    const cleanedMovies = [];
    const duplicateInfo = [];
    for (const movie of movieDetails) {
        const standardizedId = (0, exports.getStandardizedMovieId)(movie);
        if (!seen.has(standardizedId)) {
            seen.add(standardizedId);
            cleanedMovies.push(Object.assign(Object.assign({}, movie), { id: standardizedId // Ensure consistent ID
             }));
            if (movie.id !== standardizedId) {
                duplicateInfo.push({
                    originalId: movie.id || 'unknown',
                    standardizedId,
                    action: 'standardized'
                });
            }
        }
        else {
            duplicateInfo.push({
                originalId: movie.id || 'unknown',
                standardizedId,
                action: 'removed_duplicate'
            });
        }
    }
    return {
        cleanedMovies,
        duplicatesRemoved: movieDetails.length - cleanedMovies.length,
        duplicateInfo
    };
};
exports.cleanupWatchlistDuplicates = cleanupWatchlistDuplicates;
/**
 * Analyze a movie list for duplicates
 */
const analyzeMovieListForDuplicates = (movieDetails) => {
    const groups = new Map();
    const idFormats = new Set();
    movieDetails.forEach((movie, index) => {
        const standardizedId = (0, exports.getStandardizedMovieId)(movie);
        const originalId = movie.id || movie.imdbID || 'unknown';
        const title = movie.title || movie.Title || 'Unknown Title';
        // Determine ID format
        let format = 'unknown';
        if (originalId.startsWith('tt'))
            format = 'imdb';
        else if (originalId.startsWith('tmdb_'))
            format = 'tmdb_prefixed';
        else if (originalId.startsWith('tmdb'))
            format = 'tmdb_legacy';
        else if (/^\d+$/.test(originalId))
            format = 'numeric';
        else if (originalId.startsWith('title_'))
            format = 'title_based';
        idFormats.add(format);
        if (!groups.has(standardizedId)) {
            groups.set(standardizedId, []);
        }
        groups.get(standardizedId).push({
            originalId,
            title,
            index,
            formats: [format]
        });
    });
    const duplicateGroups = Array.from(groups.entries())
        .filter(([_, movies]) => movies.length > 1)
        .map(([standardizedId, movies]) => ({
        standardizedId,
        movies
    }));
    const uniqueMovies = groups.size;
    const duplicateCount = movieDetails.length - uniqueMovies;
    return {
        hasDuplicates: duplicateGroups.length > 0,
        duplicateGroups,
        analysis: {
            totalMovies: movieDetails.length,
            uniqueMovies,
            duplicateCount,
            idFormatVariations: Array.from(idFormats)
        }
    };
};
exports.analyzeMovieListForDuplicates = analyzeMovieListForDuplicates;
/**
 * Format movie runtime from minutes to hours and minutes
 */
const formatRuntime = (runtime) => {
    if (!runtime)
        return '';
    const minutes = typeof runtime === 'string' ? parseInt(runtime) : runtime;
    if (isNaN(minutes))
        return '';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 0) {
        return `${remainingMinutes}m`;
    }
    else if (remainingMinutes === 0) {
        return `${hours}h`;
    }
    else {
        return `${hours}h ${remainingMinutes}m`;
    }
};
exports.formatRuntime = formatRuntime;
/**
 * Format movie year from release date
 */
const formatMovieYear = (releaseDate) => {
    if (!releaseDate)
        return '';
    return (0, dateUtils_1.formatFirebaseDate)(releaseDate, '', { year: 'numeric' });
};
exports.formatMovieYear = formatMovieYear;
/**
 * Get movie age rating display text
 */
const formatAgeRating = (rating) => {
    if (!rating)
        return '';
    const ratingMap = {
        'G': 'General Audiences',
        'PG': 'Parental Guidance',
        'PG-13': 'Parents Strongly Cautioned',
        'R': 'Restricted',
        'NC-17': 'Adults Only',
        'NR': 'Not Rated',
        'UR': 'Unrated'
    };
    return ratingMap[rating] || rating;
};
exports.formatAgeRating = formatAgeRating;
/**
 * Calculate movie rating average from multiple sources
 */
const calculateAverageRating = (ratings) => {
    if (!ratings || ratings.length === 0)
        return 0;
    let total = 0;
    let count = 0;
    for (const rating of ratings) {
        let numericValue = 0;
        if (rating.Source === 'Internet Movie Database') {
            // IMDb rating (e.g., "8.5/10")
            const match = rating.Value.match(/^(\d+\.?\d*)/);
            if (match) {
                numericValue = parseFloat(match[1]);
            }
        }
        else if (rating.Source === 'Rotten Tomatoes') {
            // Rotten Tomatoes percentage (e.g., "85%")
            const match = rating.Value.match(/^(\d+)%/);
            if (match) {
                numericValue = parseFloat(match[1]) / 10; // Convert to 10-point scale
            }
        }
        else if (rating.Source === 'Metacritic') {
            // Metacritic score (e.g., "75/100")
            const match = rating.Value.match(/^(\d+)/);
            if (match) {
                numericValue = parseFloat(match[1]) / 10; // Convert to 10-point scale
            }
        }
        if (numericValue > 0) {
            total += numericValue;
            count++;
        }
    }
    return count > 0 ? Math.round((total / count) * 10) / 10 : 0;
};
exports.calculateAverageRating = calculateAverageRating;
